import { useEffect, useState } from 'react'

/**
 * Fetch a remote URL into memory, decode it into an `HTMLImageElement`,
 * and expose the raw bytes for the editor's save path (identity-shortcut
 * + EXIF/ICC/IPTC passthrough).
 *
 * Combines the responsibilities of the former `useFetchedBytes` and
 * `useDecodedImage` hooks into a single call: fetch → bytes + ObjectURL
 * → browser image decode → `HTMLImageElement`. Aborts on unmount / URL
 * change; revokes the internal ObjectURL on cleanup.
 *
 * This is the single hook every editor source-loader needs to turn a
 * remote image URL into a canvas-ready element + save-path metadata.
 */
export interface FetchedImage {
  /** Decoded HTMLImageElement, or `undefined` while loading / failed. */
  image: HTMLImageElement | undefined
  /** Fetched bytes, or `undefined` while loading / failed. */
  bytes: Uint8Array | undefined
  /** True while the fetch or image decode is in flight. */
  isLoading: boolean
  /**
   * Set when the fetch or decode failed (HTTP error, network error,
   * image decode error). `undefined` on abort — abort is a routine
   * effect of the URL changing, not an error condition the host should
   * react to.
   */
  error: unknown | undefined
}

export interface UseFetchedImageOptions {
  /**
   * Skip the fetch when false. Useful for "only fetch if the format is
   * browser-renderable" — the host doesn't want to waste bandwidth on a
   * HEIC original that the canvas can't decode anyway. Defaults to true.
   */
  enabled?: boolean
  /**
   * `credentials` flag passed to `fetch`. Defaults to `'omit'` —
   * cross-origin CDN fetches typically don't accept cookies and including
   * them can break cache-friendliness.
   */
  credentials?: RequestCredentials
  /** Optional MIME type for the intermediate ObjectURL Blob. */
  mimeType?: string
}

const INITIAL: FetchedImage = {
  image: undefined,
  bytes: undefined,
  isLoading: false,
  error: undefined,
}

export function useFetchedImage(
  url: string | undefined,
  options?: UseFetchedImageOptions,
): FetchedImage {
  const enabled = options?.enabled ?? true
  const credentials = options?.credentials ?? 'omit'
  const mimeType = options?.mimeType
  const [state, setState] = useState<FetchedImage>(INITIAL)

  useEffect(() => {
    if (!enabled || !url) {
      setState(INITIAL)
      return
    }

    const ac = new AbortController()
    let objectUrl: string | undefined

    setState({
      image: undefined,
      bytes: undefined,
      isLoading: true,
      error: undefined,
    })

    fetch(url, { signal: ac.signal, credentials })
      .then(r => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`)
        }
        return r.arrayBuffer()
      })
      .then(buf => {
        if (ac.signal.aborted) {
          return
        }
        const bytes = new Uint8Array(buf)
        objectUrl = URL.createObjectURL(
          new Blob([bytes], mimeType ? { type: mimeType } : undefined),
        )

        // Decode the ObjectURL into an HTMLImageElement.
        const img = new Image()
        img.crossOrigin = 'anonymous'

        img.addEventListener('load', () => {
          if (ac.signal.aborted) {
            return
          }
          setState({ image: img, bytes, isLoading: false, error: undefined })
        })

        img.addEventListener('error', () => {
          if (ac.signal.aborted) {
            return
          }
          setState({
            image: undefined,
            bytes,
            isLoading: false,
            error: new Error('Failed to decode image'),
          })
        })

        img.src = objectUrl
      })
      .catch(e => {
        if (ac.signal.aborted) {
          return
        }
        setState({
          image: undefined,
          bytes: undefined,
          isLoading: false,
          error: e,
        })
      })

    return () => {
      ac.abort()
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [url, enabled, credentials, mimeType])

  return state
}
